import FileUploader from '@/src/components/common/FileUploader/files'
import { loadArchiveFolderOptions } from '@api/loadOptions'
import { useArchiveDoc, useUploadFile } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { PencilIcon } from '@icons'
import type {
  ArchiveDocumentCreatePayload,
  ArchiveDocumentResource,
  ArchiveFolderIncluded,
  ArchiveFolderResource
} from '@type/archive.types'
import { getArchiveFacilityName, getArchivePatientName } from '@type/archive.types'
import { FormButton, FormInputField, Modal, TextAreaField } from '@ui'
import { FormSelect, Select } from '@ui/SearchInput/Select'
import { getError, getFileIconSrc, showToast } from '@util'
import moment from 'moment'
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import ArchiveStepper from './ArchiveStepper'

type Props = {
  onClose: () => void
  folder?: ArchiveFolderResource
  included?: ArchiveFolderIncluded[]
  document?: ArchiveDocumentResource
}

type UploadScannedFormValues = {
  patient_id: string
  patient_name: string
  file_number: string
  archive_folder_id: string
  title: string
  document_type: string
  date_of_service: string
  doctor_id: string
  doctor_name: string
  encounter_type: string
  diagnosis: string
  note: string
}

const UPLOAD_STEPS = [
  { id: 1, label: 'Link to file' },
  { id: 2, label: 'Document details' },
  { id: 3, label: 'Upload files' },
  { id: 4, label: 'Review' }
] as const

const DOCUMENT_TYPE_OPTIONS = [
  { label: 'Discharge', value: 'discharge' },
  { label: 'Consultation', value: 'consultation' },
  { label: 'Clinical notes', value: 'clinical_notes' },
  { label: 'Imaging', value: 'imaging' },
  { label: 'Lab Result', value: 'lab_result' },
  { label: 'Prescription', value: 'prescription' }
]

const ENCOUNTER_TYPE_OPTIONS = [
  { label: 'Outpatient', value: 'outpatient' },
  { label: 'Inpatient', value: 'inpatient' }
]

const STEP_FIELDS: Record<number, (keyof UploadScannedFormValues)[]> = {
  1: ['archive_folder_id', 'file_number'],
  2: ['title', 'document_type', 'date_of_service', 'doctor_name', 'encounter_type'],
  3: []
}

const schema = yup.object({
  patient_id: yup.string().default(''),
  patient_name: yup.string().default(''),
  file_number: yup.string().trim().required('File number is required'),
  archive_folder_id: yup.string().required('Patient / archive file is required'),
  title: yup.string().required('Document title is required'),
  document_type: yup.string().required('Document type is required'),
  date_of_service: yup.string().required('Date of service is required'),
  doctor_id: yup.string().default(''),
  doctor_name: yup.string().required('Specialty is required'),
  encounter_type: yup.string().required('Encounter type is required'),
  diagnosis: yup.string().default(''),
  note: yup.string().default('')
})

const formatBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`
}

const ReviewBlock = ({ title, onEdit, children }: { title: string; onEdit: () => void; children: ReactNode }) => (
  <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
    <div className="mb-3 flex items-center justify-between">
      <h4 className="text-xs font-semibold tracking-wide text-[#6B7280]">{title}</h4>
      <button className="flex items-center gap-1 text-xs text-[#374151]" onClick={onEdit} type="button">
        <PencilIcon />
        Edit
      </button>
    </div>
    {children}
  </div>
)

const UploadScannedDocument = ({ onClose, folder, included = [], document: existingDocument }: Props) => {
  const [step, setStep] = useState(1)
  const [files, setFiles] = useState<File[]>([])
  const [pageOrder, setPageOrder] = useState<number[]>([])
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const { createArchiveDocument } = useArchiveDoc({ enabled: false })
  const { uploadFile, isUploading } = useUploadFile()

  const defaultPatientName = folder ? getArchivePatientName(folder, included) : ''
  const defaultFacility = folder ? getArchiveFacilityName(folder, included) : ''
  const existingAttrs = existingDocument?.attributes

  const methods = useForm<UploadScannedFormValues>({
    defaultValues: {
      patient_id: folder?.relationships.patient?.data?.id || '',
      patient_name: defaultPatientName === '—' ? '' : defaultPatientName,
      file_number: existingAttrs?.file_number || '',
      archive_folder_id: folder?.id || existingDocument?.relationships.archive_folder?.data?.id || '',
      title: existingAttrs?.title || '',
      document_type: existingAttrs?.document_type || '',
      date_of_service: existingAttrs?.document_date || '',
      doctor_id: '',
      doctor_name: existingAttrs?.specialty || '',
      encounter_type: existingAttrs?.clinical_context || '',
      diagnosis: existingAttrs?.diagnosis || '',
      note: ''
    },
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { isSubmitting }
  } = methods

  const formValues = watch()

  useEffect(() => {
    setPageOrder(files.map((_, index) => index))
  }, [files])

  const documentTypeLabel = useMemo(
    () => DOCUMENT_TYPE_OPTIONS.find(o => o.value === formValues.document_type)?.label || '—',
    [formValues.document_type]
  )

  const encounterLabel = useMemo(
    () => ENCOUNTER_TYPE_OPTIONS.find(o => o.value === formValues.encounter_type)?.label || '—',
    [formValues.encounter_type]
  )

  const orderedFiles = pageOrder.map(index => files[index]).filter(Boolean)

  const handleContinue = async () => {
    if (step === 3) {
      if (files.length === 0) {
        showToast('Please upload at least one file', { type: 'error' })
        return
      }
      setStep(4)
      return
    }
    const fields = STEP_FIELDS[step]
    const isValid = await trigger(fields)
    if (isValid) setStep(current => Math.min(current + 1, 4))
  }

  const onSubmit = async (formVal: UploadScannedFormValues) => {
    if (step !== 4) return
    if (files.length === 0) {
      showToast('Please upload at least one file', { type: 'error' })
      return
    }

    try {
      const signedIds: string[] = []
      for (const file of orderedFiles) {
        const { signed_id } = await uploadFile(file)
        signedIds.push(signed_id)
      }

      const payload: ArchiveDocumentCreatePayload = {
        data: {
          type: 'archive_document',
          attributes: {
            archive_folder_id: Number(formVal.archive_folder_id),
            file_number: formVal.file_number,
            title: formVal.title,
            document_type: formVal.document_type,
            specialty: formVal.doctor_name || undefined,
            document_date: formVal.date_of_service,
            clinical_context: formVal.encounter_type || undefined,
            diagnosis: formVal.diagnosis || undefined,
            file_signed_ids: signedIds
          }
        }
      }

      await createArchiveDocument(payload)
      showToast('Scanned document uploaded successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (step < 4) {
      void handleContinue()
      return
    }
    void handleSubmit(onSubmit)()
  }

  const handleDragStart = (index: number) => setDragIndex(index)
  const handleDrop = (index: number) => {
    if (dragIndex === null || dragIndex === index) return
    setPageOrder(prev => {
      const next = [...prev]
      const [moved] = next.splice(dragIndex, 1)
      next.splice(index, 0, moved)
      return next
    })
    setDragIndex(null)
  }

  const initials = formValues.patient_name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('')

  return (
    <Modal
      className="md:min-w-[calc(640px, 100vw-4rem)] md:w-[640px]"
      onClose={onClose}
      title={existingDocument ? 'Edit Scanned Document' : 'Upload Scanned Document'}
    >
      <FormProvider {...methods}>
        <form className="flex h-full flex-col overflow-auto" onSubmit={handleFormSubmit}>
          <ArchiveStepper currentStep={step} steps={UPLOAD_STEPS} />

          <div className="flex-1 overflow-y-auto px-8 py-6">
            {step === 1 && (
              <div className="flex flex-col gap-4">
                <Controller
                  control={control}
                  name="archive_folder_id"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      borderRadius="100px"
                      defaultOptions
                      error={error?.message}
                      getOptionLabel={({ label }) => label}
                      getOptionValue={({ value: optionValue }) => optionValue}
                      isAsync
                      label="Patient Name"
                      loadOptions={loadArchiveFolderOptions}
                      onChange={(selectedOption: any) => {
                        onChange(selectedOption?.value || '')
                        setValue('patient_name', selectedOption?.label || '')
                        setValue('patient_id', selectedOption?.patient_id || '')
                        if (selectedOption?.folder_number && !formValues.file_number) {
                          setValue('archive_folder_id', selectedOption.value, { shouldValidate: true })
                        }
                      }}
                      placeholder="Enter patient's name"
                      value={value ? { value, label: formValues.patient_name || value } : undefined}
                    />
                  )}
                />
                <FormInputField label="File number" name="file_number" placeholder="E.g GH/4421" />
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-4">
                <p className="text-sm font-semibold text-[#111827]">Document Details</p>
                <FormInputField label="Document Title" name="title" placeholder='e.g "Admission note 2023"' />
                <FormSelect
                  getOptionLabel={({ label }) => label}
                  getOptionValue={({ value }) => value}
                  isCreatable
                  label="Document Type"
                  name="document_type"
                  options={DOCUMENT_TYPE_OPTIONS}
                  placeholder="Select type"
                />
                <FormInputField label="Date of Service" name="date_of_service" type="date" />
                <FormInputField label="Staff" name="doctor_name" placeholder="E.g Pathology" />
                <FormSelect
                  getOptionLabel={({ label }) => label}
                  getOptionValue={({ value }) => value}
                  label="Encounter type"
                  name="encounter_type"
                  options={ENCOUNTER_TYPE_OPTIONS}
                  placeholder="Select a type"
                />
                <div className="mt-2">
                  <p className="mb-3 text-sm font-semibold text-[#111827]">Clinical Context</p>
                  <div className="flex flex-col gap-4">
                    <FormInputField label="Diagnosis" name="diagnosis" placeholder="E.g Hypertension" />
                    <TextAreaField label="Note (Optional)" name="note" placeholder="" />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-4">
                <FileUploader
                  accept={{
                    'image/jpeg': ['.jpg', '.jpeg'],
                    'image/png': ['.png'],
                    'application/pdf': ['.pdf']
                  }}
                  cautionMessage={null}
                  files={files}
                  onFileRemove={file => setFiles(prev => prev.filter(f => f !== file))}
                  onFileUpload={file => setFiles(prev => [...prev, file])}
                />
                {/* {files.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {files.map(file => (
                      <div
                        className="flex items-center justify-between rounded-lg border border-[#E5E7EB] px-3 py-2"
                        key={`${file.name}-${file.size}`}
                      >
                        <div className="flex items-center gap-2">
                          <img alt="" className="h-6 w-6" src={getFileIconSrc(file.name)} />
                          <div>
                            <p className="text-sm font-medium text-[#111827]">{file.name}</p>
                            <p className="text-xs text-[#9CA3AF]">{formatBytes(file.size)}</p>
                          </div>
                        </div>
                        <button onClick={() => setFiles(prev => prev.filter(f => f !== file))} type="button">
                          <Close color="#9CA3AF" size="small" />
                        </button>
                      </div>
                    ))}
                  </div>
                )} */}
                {files.length > 0 && (
                  <div className="rounded-xl border border-[#E5E7EB] p-4">
                    <p className="text-sm font-semibold text-[#111827]">Page Order</p>
                    <p className="mt-1 text-xs text-[#6B7280]">Each uploaded file becomes one page. Drag to reorder.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {pageOrder.map((fileIndex, orderIndex) => {
                        const file = files[fileIndex]
                        if (!file) return null
                        return (
                          <button
                            className="cursor-grab rounded-full border border-[#D1D5DB] bg-[#F9FAFB] px-3 py-1.5 text-xs font-medium text-[#374151]"
                            draggable
                            key={`${file.name}-${fileIndex}`}
                            onDragOver={e => e.preventDefault()}
                            onDragStart={() => handleDragStart(orderIndex)}
                            onDrop={() => handleDrop(orderIndex)}
                            type="button"
                          >
                            {orderIndex + 1} {file.name}
                          </button>
                        )
                      })}
                    </div>
                    <p className="mt-3 text-xs text-[#9CA3AF]">
                      Position controls the order pages appear in the document viewer.
                    </p>
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="flex flex-col gap-4">
                <ReviewBlock onEdit={() => setStep(1)} title="LINKED PHYSICAL FILE">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F3F4F6] text-sm font-semibold text-[#374151]">
                      {initials || '—'}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#111827]">{formValues.patient_name || '—'}</p>
                      <p className="text-xs text-[#6B7280]">
                        {[formValues.file_number, defaultFacility].filter(Boolean).join(' · ') || '—'}
                      </p>
                      <p className="text-xs text-[#9CA3AF]">File Number: {formValues.file_number || '—'}</p>
                    </div>
                  </div>
                </ReviewBlock>

                <ReviewBlock onEdit={() => setStep(2)} title="DOCUMENT DETAILS">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <p className="text-xs text-[#9CA3AF]">Title</p>
                      <p className="text-sm font-medium text-[#111827]">{formValues.title || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#9CA3AF]">Type</p>
                      <p className="text-sm font-medium text-[#111827]">{documentTypeLabel}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#9CA3AF]">Date of service</p>
                      <p className="text-sm font-medium text-[#111827]">
                        {formValues.date_of_service ? moment(formValues.date_of_service).format('D MMM, YYYY') : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#9CA3AF]">Specialty</p>
                      <p className="text-sm font-medium text-[#111827]">{formValues.doctor_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#9CA3AF]">Encounter</p>
                      <p className="text-sm font-medium text-[#111827]">{encounterLabel}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#9CA3AF]">Diagnosis</p>
                      <p className="text-sm font-medium text-[#111827]">{formValues.diagnosis || '—'}</p>
                    </div>
                  </div>
                </ReviewBlock>

                <ReviewBlock onEdit={() => setStep(3)} title="FILE UPLOADED">
                  <div className="flex flex-col gap-2">
                    {orderedFiles.map(file => (
                      <div className="flex items-center gap-2" key={`${file.name}-${file.size}`}>
                        <img alt="" className="h-6 w-6" src={getFileIconSrc(file.name)} />
                        <div>
                          <p className="text-sm font-medium text-[#111827]">{file.name}</p>
                          <p className="text-xs text-[#9CA3AF]">{formatBytes(file.size)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ReviewBlock>
              </div>
            )}
          </div>

          <FormButton
            cancel={step === 1 ? 'Cancel' : 'Back'}
            isLoading={isSubmitting || isUploading}
            onClose={step === 1 ? onClose : () => setStep(current => current - 1)}
            submit={step === 4 ? 'Save Record' : 'Continue'}
            type="submit"
          />
        </form>
      </FormProvider>
    </Modal>
  )
}

export default UploadScannedDocument
